import { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { Envelope } from "react-bootstrap-icons";
import { isAxiosError } from "axios";
import { emailResourceHealthSnapshots } from "@/services/resourceComplianceService";
import { snapshotLabel } from "./resourceHealthDemo";
import type { SnapshotResult } from "./resourceHealthDemo";

export default function EmailSnapshotButton({ personEmail, results }: { personEmail: string; results: SnapshotResult[] }) {
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setError(null);
    try {
      await emailResourceHealthSnapshots(personEmail, results.map((result) => result.snapshot.snapshotId));
      if (mounted.current) { setQueued(true); setConfirm(false); }
    } catch (failure) {
      if (!mounted.current) return;
      const status = isAxiosError(failure) ? failure.response?.status : undefined;
      setError(status === 429
        ? "A snapshot email was already queued for this person in the last five minutes. Please wait before sending again."
        : status === 403 ? "Your account does not have permission to email snapshots."
        : status === 404 ? "This person is no longer available in the selected snapshots. Start over and try again."
        : "Unable to confirm the email was queued. Please wait five minutes before trying again, or contact support if this continues.");
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return <>
    <div className="d-flex justify-content-end">
      <Button onClick={() => setConfirm(true)} disabled={queued || !results.some((result) => result.person)}>
        <Envelope aria-hidden="true" className="me-2" />{queued ? "Email queued" : "Email me my snapshot"}
      </Button>
    </div>
    {queued && <span role="status" className="small text-success align-self-center">Queued for {personEmail}</span>}
    <Modal show={confirm} onHide={() => { if (!sending) setConfirm(false); }} backdrop={sending ? "static" : true} keyboard={!sending} centered>
      <Modal.Header closeButton={!sending}><Modal.Title>Email your Resource Health snapshot</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>Send your summary and owned-resource details to <strong>{personEmail}</strong>?</p>
        <ul>{results.map((result) => <li key={result.snapshot.snapshotId}>{snapshotLabel(result.snapshot)}</li>)}</ul>
        <p className="small text-muted mb-0">Includes the displayed snapshot{results.length > 1 ? " comparison" : ""}. The recipient cannot be changed here. Standard system email copies apply.</p>
        {error && <Alert variant="danger" role="alert" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setConfirm(false)} disabled={sending}>Cancel</Button>
        <Button onClick={send} disabled={sending}>
          {sending && <Spinner animation="border" size="sm" className="me-2" />} {sending ? "Queuing..." : "Send my snapshot"}
        </Button>
      </Modal.Footer>
    </Modal>
  </>;
}
